import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import {
  widgetContainer,
  widgetVisible,
  widgetHidden,
  aiChatCircle,
} from "./floating-ai-widget.module.css"
import { trackEvent } from "../../modules/analytics"


const FloatingAIWidget = ({ targetRef, positionBottom = 20 }) => {
  const [isVisible, setIsVisible] = useState(true) // Initially visible
  const observerRef = useRef(null)

  useEffect(() => {
    // Set up IntersectionObserver to track when target element is in viewport
    if (targetRef && targetRef.current && typeof window !== "undefined" && "IntersectionObserver" in window) {
      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          // Hide widget when target is in view, show when it's not
          setIsVisible(!entry.isIntersecting)
        },
        {
          threshold: 0.3, // Trigger when 30% of the target is visible
          rootMargin: '-50px 0px' // Add some margin for better UX
        }
      )

      observerRef.current.observe(targetRef.current)
    }

    // Cleanup observer on unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [targetRef])

  const handleButtonClick = () => {
    trackEvent("ai_button_click");
    if (targetRef && targetRef.current) {
      // Scroll to the AI widget section smoothly
      targetRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
      
      // Focus on the element for accessibility
      targetRef.current.focus({ preventScroll: true })
    }
  }

  // Apply visibility class based on state
  const containerClass = `${widgetContainer} ${isVisible ? widgetVisible : widgetHidden}`

  return (
    <div className={containerClass} style={{ bottom: positionBottom + "px" }}>
      {/* AI Focus Button */}
      <button
        className={aiChatCircle}
        onClick={handleButtonClick}
        type="button"
        aria-label="Ga naar AI assistent"
        title="Ga naar AI assistent"
      >
        <svg viewBox="0 0 24 24" width="32" height="32">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.1266 22.1995C16.7081 22.5979 17.4463 23.0228 18.3121 23.3511C19.9903 23.9874 21.244 24.0245 21.8236 23.9917C23.1167 23.9184 23.2907 23.0987 22.5972 22.0816C21.8054 20.9202 21.0425 19.6077 21.1179 18.1551C22.306 16.3983 23 14.2788 23 12C23 5.92487 18.0751 1 12 1C5.92487 1 1 5.92487 1 12C1 18.0751 5.92487 23 12 23C13.4578 23 14.8513 22.7159 16.1266 22.1995ZM12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21C13.3697 21 14.6654 20.6947 15.825 20.1494C16.1635 19.9902 16.5626 20.0332 16.8594 20.261C17.3824 20.6624 18.1239 21.1407 19.0212 21.481C19.4111 21.6288 19.7674 21.7356 20.0856 21.8123C19.7532 21.2051 19.4167 20.4818 19.2616 19.8011C19.1018 19.0998 18.8622 17.8782 19.328 17.2262C20.3808 15.7531 21 13.9503 21 12C21 7.02944 16.9706 3 12 3Z"
            fill="#fff"
            opacity="0.8"
          />
          <text
            x="12"
            y="15"
            opacity="0.8"
            textAnchor="middle"
            fontSize="8"
            fontWeight="700"
            fill="currentColor"
            fontFamily="Arial, sans-serif"
          >
            AI
          </text>
        </svg>
      </button>
    </div>
  )
}

FloatingAIWidget.propTypes = {
  targetRef: PropTypes.object.isRequired,
  positionBottom: PropTypes.number,
}

export default FloatingAIWidget
